import React from 'react';
import EmptyState from '../../../components/ui/EmptyState';
import Icon from '../../../components/AppIcon';
import { formatDate } from '../../../utils/formatters';

const PaperList = ({ papers, selectedPaper, onSelectPaper, minReviews, filtered = false, onClearFilters }) => {
  const getPriorityColor = (paper) => {
    const assigned = paper?.assignedReviewers?.length || 0;
    if (assigned === 0) return 'border-l-4 border-l-error';
    if (assigned < minReviews) return 'border-l-4 border-l-warning';
    return 'border-l-4 border-l-success';
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString, { month: 'short', day: 'numeric' }) || 'N/A';
  };

  if (!papers?.length) {
    return (
      <EmptyState
        compact
        filtered={filtered}
        onClear={onClearFilters}
        icon="FileText"
        title={filtered ? 'No papers match these filters' : 'No papers to assign'}
        body={filtered
          ? 'Try a broader search, or clear the filters to see every paper.'
          : 'Papers appear here once authors submit to this conference.'}
      />
    );
  }

  return (
    <div className="divide-y divide-border">
      {papers?.map((paper) => (
        <div
          key={paper?.id}
          className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
            selectedPaper?.id === paper?.id ? 'bg-primary-subtle' : ''
          } ${getPriorityColor(paper)}`}
          onClick={() => onSelectPaper(paper)}
        >
          <h3 className="text-sm font-semibold text-foreground line-clamp-2 mb-2">
            {paper?.title}
          </h3>
          
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>{paper?.authorName}</span>
            <span>{formatDate(paper?.submittedAt)}</span>
          </div>

          {paper?.trackName && (
            <div className="mb-2">
              <span className="inline-flex items-center px-2 py-1 text-xs bg-primary/10 text-primary">
                {paper?.trackName}
              </span>
            </div>
          )}

          {paper?.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {paper?.keywords?.slice(0, 3)?.map((keyword, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-0.5 text-xs bg-muted text-foreground"
                >
                  {keyword}
                </span>
              ))}
              {paper?.keywords?.length > 3 && (
                <span className="text-xs text-muted-foreground">+{paper?.keywords?.length - 3}</span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={14} className="text-muted-foreground" />
              <span className="text-xs text-foreground font-medium">
                {paper?.assignedReviewers?.length}/{paper?.requiredReviewers}
              </span>
            </div>
            {paper?.isFullyAssigned && (
              <Icon name="CheckCircle2" size={14} className="text-success" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PaperList;
